// Bounded, rate-limited negative cache for unknown-CID lookups (szzt-spec.md §12, threat A.4 #12).
//
// §12 requires unknown-CID lookups (a CID absent from a signed index) be BOTH negative-cached and
// rate-limited, to bound cache-miss amplification: a flood of distinct, non-existent CIDs must not force
// unbounded site re-resolution nor grow gateway memory without limit. This structure provides both:
//   - a bounded, insertion-ordered LRU of known-unknown (site, cid) pairs: repeats are refused in O(1),
//     and the set can never exceed `maxEntries` (evicts oldest → fixed memory);
//   - a single global token bucket (injected clock, mirroring server `TokenBucketLimiter`), bounding the
//     RATE at which NEW distinct unknown lookups are admitted; over the limit, `record` returns
//     "rate-limited" and the caller refuses with 429 instead of 404.
//
// The LRU is keyed by (SITE, cid), not cid alone (roadmap H7). "CID X is not in the index" is a
// site-RELATIVE fact — X can be absent from site A yet be a legitimately-public block of site B — so a
// global cid-only key let a lookup at site A's origin negative-cache X and then mask site B's own signed
// serve of X (cross-site poisoning, 200 → 404). Scoping the key to the resolved site's identity makes that
// structurally impossible: a fact recorded under one site can never be read back under another. The token
// bucket stays GLOBAL on purpose — it bounds total gateway fan-out, which is not a per-site quantity, and
// reads are unauthenticated so the serving core has no per-client identity to key it by.
//
// Verification is untouched — this concerns neither bytes nor CIDs, only whether/when an unresolvable
// CID is refused. The clock is the sole ambient dependency (defaults to Date.now, injectable for tests).

/** Default max distinct known-unknown CIDs retained; oldest-inserted evicted past this. Tunable. */
export const DEFAULT_MAX_NEGATIVE_ENTRIES = 10_000;
/** Default burst of NEW unknown-CID lookups admitted before the sustained rate applies. Tunable. */
export const DEFAULT_NEGATIVE_BURST = 100;
/** Default sustained admission rate for NEW unknown CIDs, per second. Tunable. */
export const DEFAULT_NEGATIVE_REFILL_PER_SECOND = 50;

export interface NegativeCacheOptions {
  /** Injected clock (epoch ms). Defaults to Date.now — the sole ambient dependency; tests pass a fake. */
  now?: () => number;
  /** Max retained known-unknown CIDs (LRU by insertion). Positive integer. */
  maxEntries?: number;
  /** Token-bucket burst capacity for NEW unknown-CID admissions. Positive integer. */
  capacity?: number;
  /** Token-bucket refill, tokens/second. Finite, > 0. */
  refillPerSecond?: number;
}

/** Verdict from {@link NegativeCache.record}: whether the caller may proceed or must refuse (429). */
export type NegativeVerdict = "admit" | "rate-limited";

/**
 * Negative cache: a bounded LRU of known-unknown (site, cid) pairs plus one GLOBAL token bucket gating
 * admission of NEW unknown lookups. See the module comment for the §12 rationale + the H7 (site, cid)
 * keying. The LRU membership is per-(site, cid); the rate budget is a single shared bucket.
 */
export class NegativeCache {
  readonly #now: () => number;
  readonly #maxEntries: number;
  readonly #capacity: number;
  readonly #refillPerMs: number;
  /** Insertion-ordered; the first key is the oldest (LRU eviction target). Value is a marker. */
  readonly #entries = new Map<string, true>();
  #tokens: number;
  /** Epoch ms of the last token refill; lazily set on first `record` so the constructor calls no clock. */
  #lastMs: number | undefined;

  constructor(opts: NegativeCacheOptions = {}) {
    const maxEntries = opts.maxEntries ?? DEFAULT_MAX_NEGATIVE_ENTRIES;
    if (!Number.isInteger(maxEntries) || maxEntries < 1) {
      throw new RangeError(`negative-cache maxEntries must be an integer >= 1, got ${maxEntries}`);
    }
    const capacity = opts.capacity ?? DEFAULT_NEGATIVE_BURST;
    if (!Number.isInteger(capacity) || capacity < 1) {
      throw new RangeError(`negative-cache capacity must be an integer >= 1, got ${capacity}`);
    }
    const refillPerSecond = opts.refillPerSecond ?? DEFAULT_NEGATIVE_REFILL_PER_SECOND;
    if (!Number.isFinite(refillPerSecond) || refillPerSecond <= 0) {
      throw new RangeError(`negative-cache refillPerSecond must be a finite number > 0, got ${refillPerSecond}`);
    }
    this.#now = opts.now ?? (() => Date.now());
    this.#maxEntries = maxEntries;
    this.#capacity = capacity;
    this.#refillPerMs = refillPerSecond / 1000;
    this.#tokens = capacity;
  }

  /**
   * The per-(site, cid) LRU key (roadmap H7). `site` is a resolved-index identity (its root CID); joining
   * with a NUL (\x00) — which appears in neither a base32/base58 CID nor the root-CID site identity — means
   * the two components can never be confused across the boundary, so a fact recorded for one site is
   * structurally unreachable under another. Callers pass the two parts separately and never a pre-joined
   * string, so the scoping cannot be spoofed by a caller.
   */
  #key(site: string, cid: string): string {
    return `${site}\x00${cid}`;
  }

  /** True if this (site, cid) is already known-unknown (negative-cached). O(1); never mutates. */
  has(site: string, cid: string): boolean {
    return this.#entries.has(this.#key(site, cid));
  }

  /**
   * Record an unknown-CID lookup for a resolved site: remember (site, cid) (bounded LRU, refreshing
   * recency) and, for a pair not already cached, charge one token from the GLOBAL admission bucket.
   * Returns "rate-limited" when the bucket is empty (caller refuses with 429), else "admit". A repeat of
   * an already-cached pair refreshes recency and returns "admit" WITHOUT charging — only a genuinely new
   * unknown (site, cid) consumes the rate budget. The bucket is global, so distinct unknowns across ALL
   * sites still share one fan-out budget.
   */
  record(site: string, cid: string): NegativeVerdict {
    const key = this.#key(site, cid);
    const seen = this.#entries.delete(key); // drop-then-set re-inserts at the newest position (LRU refresh)
    this.#entries.set(key, true);
    while (this.#entries.size > this.#maxEntries) {
      const oldest = this.#entries.keys().next().value;
      if (oldest === undefined) break;
      this.#entries.delete(oldest);
    }
    if (seen) return "admit";
    return this.#consumeToken() ? "admit" : "rate-limited";
  }

  /** Lazily refill against the injected clock, then consume one token if available. */
  #consumeToken(): boolean {
    const nowMs = this.#now();
    if (this.#lastMs === undefined) this.#lastMs = nowMs;
    const elapsedMs = nowMs - this.#lastMs;
    this.#tokens = Math.min(this.#capacity, this.#tokens + elapsedMs * this.#refillPerMs);
    this.#lastMs = nowMs;
    if (this.#tokens >= 1) {
      this.#tokens -= 1;
      return true;
    }
    return false;
  }

  /** Current retained known-unknown CID count (introspection/tests). */
  get size(): number {
    return this.#entries.size;
  }
}
